import * as React from 'react'
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, Chip,
    CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, ListItemButton,
    ListItemText, MenuItem, Paper, Stack, Switch, TextField, Typography
} from '@mui/material'
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LocalActivityRoundedIcon from '@mui/icons-material/LocalActivityRounded'
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'

import useAutoTranslate from '../hooks/useAutoTranslate'

const fieldSx = {
    '& .MuiOutlinedInput-root': { minHeight: 42, borderRadius: '8px', backgroundColor: 'rgba(17,29,49,.72)' },
    '& .MuiInputBase-input, & .MuiSelect-select': { color: '#f5f7ff', fontWeight: 650 }
}
const panelSx = {
    p: { xs: 2, md: 2.5 }, borderRadius: '11px', border: '1px solid rgba(129,146,178,.24)',
    backgroundColor: 'rgba(20,32,53,.56)', boxShadow: 'none'
}

function HelpText({ children }) {
    return <Stack direction="row" spacing={.7} alignItems="flex-start" sx={{ mt: .7 }}>
        <InfoOutlinedIcon sx={{ mt: '2px', fontSize: 14, color: '#7f93b5' }} />
        <Typography sx={{ color: '#7f93b5', fontSize: '.79rem', lineHeight: 1.45 }}>{children}</Typography>
    </Stack>
}

function SwitchSetting({ checked, onChange, label, helper, disabled = false }) {
    return <Box sx={{ opacity: disabled ? .6 : 1 }}>
        <Stack direction="row" spacing={1.1} alignItems="center" justifyContent="space-between">
            <Typography sx={{ color: '#f5f7ff', fontSize: '.93rem', fontWeight: 800 }}>{label}</Typography>
            <Switch checked={Boolean(checked)} disabled={disabled} onChange={event => onChange(event.target.checked)} />
        </Stack>
        {helper ? <HelpText>{helper}</HelpText> : null}
    </Box>
}

function LabeledField({ label, helper, children }) {
    return <Box>
        <Typography sx={{ mb: .65, color: '#f5f7ff', fontSize: '.82rem', fontWeight: 800 }}>{label}</Typography>
        {children}
        {helper ? <HelpText>{helper}</HelpText> : null}
    </Box>
}

function TournamentAccordion({ title, subtitle, children, defaultExpanded = false }) {
    return <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0} sx={{
        color: '#f5f7ff', backgroundColor: 'transparent', borderTop: '1px solid rgba(129,146,178,.13)',
        '&:before': { display: 'none' }, '&.Mui-expanded': { m: 0 }
    }}>
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: '#9eb0cd' }} />} sx={{ px: .3, minHeight: 52, '&.Mui-expanded': { minHeight: 52 } }}>
            <Box><Typography sx={{ fontSize: '.93rem', fontWeight: 850 }}>{title}</Typography>
                {subtitle ? <Typography sx={{ mt: .25, color: '#7f93b5', fontSize: '.76rem' }}>{subtitle}</Typography> : null}</Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: .3, pt: 0, pb: 2.2 }}>{children}</AccordionDetails>
    </Accordion>
}

const formatDuration = seconds => {
    const value = Number(seconds) || 0
    if (value >= 86400 && value % 86400 === 0) return `${value / 86400}d`
    if (value >= 3600 && value % 3600 === 0) return `${value / 3600}h`
    return `${Math.round(value / 60)}m`
}

function QuestPicker({ label, helper, selectedIds, onChange, catalog, loading, loadError, t }) {
    const [open, setOpen] = React.useState(false)
    const [search, setSearch] = React.useState('')
    const [category, setCategory] = React.useState('all')
    const [tier, setTier] = React.useState('all')
    const [pickedOnly, setPickedOnly] = React.useState(false)
    const [draft, setDraft] = React.useState([])
    const selected = Array.isArray(selectedIds) ? selectedIds.map(String) : []
    const categories = React.useMemo(() => Array.from(new Map(catalog.map(quest => [quest.category, quest.categoryLabel])).entries()), [catalog])
    const tiers = React.useMemo(() => Array.from(new Set(catalog.map(quest => quest.tier).filter(Boolean))), [catalog])
    const draftSet = new Set(draft)
    const filtered = catalog.filter(quest => {
        if (pickedOnly && !draftSet.has(String(quest.allianceQuestID))) return false
        if (category !== 'all' && quest.category !== category) return false
        if (tier !== 'all' && quest.tier !== tier) return false
        const query = search.trim().toLocaleLowerCase()
        return !query || `${quest.title} ${quest.description} ${quest.target} ${quest.allianceQuestID} ${quest.questID}`.toLocaleLowerCase().includes(query)
    })
    const openDialog = () => {
        setDraft(selected); setSearch(''); setCategory('all'); setTier('all'); setPickedOnly(false); setOpen(true)
    }
    const toggle = id => setDraft(current => current.includes(String(id)) ? current.filter(value => value !== String(id)) : [...current, String(id)])

    return <LabeledField label={label} helper={helper}>
        <Button fullWidth variant="outlined" onClick={openDialog} startIcon={<PlaylistAddCheckRoundedIcon />} sx={{
            minHeight: 44, px: 1.4, justifyContent: 'flex-start', color: '#f5f7ff',
            borderColor: 'rgba(129,146,178,.42)', backgroundColor: 'rgba(17,29,49,.72)', textTransform: 'none', fontWeight: 800
        }}>
            {t('Choose quests')}<Chip size="small" label={`${selected.length} ${t('selected')}`} sx={{ ml: 'auto' }} />
        </Button>
        <Typography sx={{ mt: .6, color: '#7f93b5', fontSize: '.75rem' }}>
            {selected.length ? `${selected.length} ${t('quests selected')}` : t('No quests picked yet.')}
        </Typography>

        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
            <DialogTitle>{t('Choose Grand Tournament quests')}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.25}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                        <TextField fullWidth size="small" placeholder={t('Search by name or requirement')} value={search}
                            onChange={event => setSearch(event.target.value)} InputProps={{ startAdornment: <SearchRoundedIcon sx={{ mr: 1, color: '#7f93b5' }} /> }} />
                        <Button variant={pickedOnly ? 'contained' : 'outlined'} onClick={() => setPickedOnly(value => !value)}>{t('Picked only')}</Button>
                        <Button onClick={() => setDraft([])}>{t('Clear all')}</Button>
                    </Stack>
                    <Stack direction="row" spacing={.7} flexWrap="wrap" useFlexGap>
                        <Button size="small" variant={category === 'all' ? 'contained' : 'outlined'} onClick={() => setCategory('all')}>{t('All types')}</Button>
                        {categories.map(([key, name]) => <Button key={key} size="small" variant={category === key ? 'contained' : 'outlined'} onClick={() => setCategory(key)}>{name}</Button>)}
                    </Stack>
                    <Stack direction="row" spacing={.7} flexWrap="wrap" useFlexGap>
                        <Button size="small" variant={tier === 'all' ? 'contained' : 'outlined'} onClick={() => setTier('all')}>{t('All tiers')}</Button>
                        {tiers.map(name => <Button key={name} size="small" variant={tier === name ? 'contained' : 'outlined'} onClick={() => setTier(name)}>{name}</Button>)}
                    </Stack>
                    {loading ? <Box sx={{ minHeight: 220, display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box> :
                        loadError ? <Alert severity="error">{t('The Grand Tournament quest catalog could not be loaded.')}</Alert> :
                            !filtered.length ? <Alert severity="info">{t('No quests found.')}</Alert> :
                                <Paper variant="outlined" sx={{ maxHeight: 490, overflow: 'auto', borderColor: 'rgba(129,146,178,.24)' }}>
                                    <ListItemButton onClick={() => setDraft(Array.from(new Set([...draft, ...filtered.map(quest => String(quest.allianceQuestID))])))}>
                                        <ListItemText primary={t('Select all results')} secondary={`${filtered.length} ${t('quests')}`} />
                                    </ListItemButton>
                                    {filtered.map(quest => <ListItemButton key={quest.allianceQuestID} divider onClick={() => toggle(quest.allianceQuestID)}>
                                        <Checkbox edge="start" checked={draftSet.has(String(quest.allianceQuestID))} tabIndex={-1} />
                                        <ListItemText primary={<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <Typography fontWeight={850}>{quest.title}</Typography><Chip size="small" label={`${quest.rewardPoints} ${t('points')}`} color="primary" variant="outlined" />
                                        </Stack>} secondary={`${quest.description} · ${quest.tier} · ${formatDuration(quest.durationSeconds)} · ID ${quest.allianceQuestID}`} />
                                    </ListItemButton>)}
                                </Paper>}
                    <Typography variant="caption" color="text.secondary">{draft.length} {t('selected')} · {catalog.length} {t('official AME quests')}</Typography>
                </Stack>
            </DialogContent>
            <DialogActions><Button onClick={() => setOpen(false)}>{t('Cancel')}</Button>
                <Button variant="contained" onClick={() => { onChange(draft); setOpen(false) }}>{t('Apply selection')}</Button></DialogActions>
        </Dialog>
    </LabeledField>
}

const divisionNames = { 1: 'Copper', 2: 'Glass', 3: 'Bronze', 4: 'Silver', 5: 'Gold', 6: 'Diamond' }

function LiveTournament({ status, enabled, catalog }) {
    const active = Boolean(status?.active)
    const slots = status?.questBoard?.slots ?? []
    const current = slots.find(slot => slot.state === 'assigned_self' || slot.state === 'collect')
    const currentQuest = current ? catalog.find(quest => String(quest.allianceQuestID) === String(current.questID)) : undefined
    return <Stack spacing={1.35}>
        <Alert severity={active ? (enabled ? 'success' : 'warning') : 'info'}>
            {active ? (enabled ? 'Grand Tournament is active and automation is enabled.' : 'Grand Tournament is active, but automation is disabled.') : 'No active Grand Tournament was reported by the game.'}
        </Alert>
        <Grid container spacing={1.1}>
            {[
                ['Division', divisionNames[status?.leagueID] ?? (status?.leagueID ? `League ${status.leagueID}` : '—')],
                ['Personal points', Number(status?.personalPoints ?? 0).toLocaleString()],
                ['Alliance points', Number(status?.alliancePoints ?? 0).toLocaleString()],
                ['Quest Tickets', Number(status?.questTickets ?? 0).toLocaleString()]
            ].map(([label, value]) => <Grid item xs={6} md={3} key={label}><Paper variant="outlined" sx={{ p: 1.15, borderColor: 'rgba(129,146,178,.2)', background: 'rgba(14,25,44,.35)' }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography><Typography fontWeight={850}>{value}</Typography>
            </Paper></Grid>)}
        </Grid>
        <Paper variant="outlined" sx={{ p: 1.2, borderColor: 'rgba(129,146,178,.2)', background: 'rgba(14,25,44,.35)' }}>
            <Typography variant="caption" color="text.secondary">Current quest</Typography>
            <Typography fontWeight={850}>{currentQuest?.title ?? (current ? `Quest ${current.questID}` : 'No quest is running')}</Typography>
            {currentQuest ? <Typography variant="body2" color="text.secondary">{currentQuest.description}</Typography> : null}
        </Paper>
        <Grid container spacing={1.1}>
            <Grid item xs={12} md={6}><Paper variant="outlined" sx={{ p: 1.2, borderColor: 'rgba(129,146,178,.2)' }}>
                <Typography variant="caption" color="text.secondary">Quest board</Typography>
                <Typography fontWeight={800}>{status?.questBoard?.statusText ?? 'Waiting for game data'}</Typography>
            </Paper></Grid>
            <Grid item xs={12} md={6}><Paper variant="outlined" sx={{ p: 1.2, borderColor: 'rgba(129,146,178,.2)' }}>
                <Typography variant="caption" color="text.secondary">Extra Quest Ticket</Typography>
                <Typography fontWeight={800}>{status?.ticketPurchase?.state ?? 'idle'} · 7,500 rubies</Typography>
            </Paper></Grid>
        </Grid>
        {status?.decisions?.length ? <Box><Typography fontWeight={850} sx={{ mb: .5 }}>Recent decisions</Typography>
            {status.decisions.slice(0, 8).map((item, index) => <Typography key={item.id ?? index} variant="body2" color="text.secondary">{item.action} · {item.message}</Typography>)}</Box> : null}
    </Stack>
}

export default function GrandTournamentPanel({ value, enabled, runtimeStatus, onEnabledChange, onChange, appLanguage }) {
    const t = useAutoTranslate(appLanguage, [
        'Grand Tournament settings', 'Enable Grand Tournament', 'Alliance quests', 'Swap Unwanted Tournament Quests',
        'Rubies When Swapping', 'Watch my ruby limit', 'Wait for a free swap', 'Keep At Least This Many Rubies',
        'Stop At Enough Quests To Keep', 'Quests To Keep On The Board', 'Quests To Swap', 'Take On Tournament Quests',
        'Quests To Take On', 'Collect Finished Quests', 'Quest Ticket', 'Buy The Additional Quest Ticket',
        'Quest Ticket Ruby Reserve', 'Choose quests', 'selected', 'quests selected', 'No quests picked yet.',
        'Choose Grand Tournament quests', 'Search by name or requirement', 'Picked only', 'Clear all', 'All types',
        'All tiers', 'No quests found.', 'The Grand Tournament quest catalog could not be loaded.', 'Select all results',
        'quests', 'points', 'official AME quests', 'Cancel', 'Apply selection'
    ])
    const settings = value && typeof value === 'object' ? value : {}
    const update = (key, nextValue) => onChange?.({ ...settings, [key]: nextValue })
    const [catalog, setCatalog] = React.useState([])
    const [loading, setLoading] = React.useState(true)
    const [loadError, setLoadError] = React.useState(false)

    React.useEffect(() => {
        const controller = new AbortController()
        fetch('/grand-tournament-quest-catalog.json', { signal: controller.signal })
            .then(response => { if (!response.ok) throw new Error(String(response.status)); return response.json() })
            .then(payload => setCatalog(Array.isArray(payload) ? payload : []))
            .catch(error => { if (error?.name !== 'AbortError') { setCatalog([]); setLoadError(true) } })
            .finally(() => { if (!controller.signal.aborted) setLoading(false) })
        return () => controller.abort()
    }, [])

    return <Stack spacing={2.1}>
        <Paper sx={panelSx}><Stack spacing={1.7}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center"><EmojiEventsRoundedIcon sx={{ color: '#f0c35b' }} />
                    <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 850 }}>Live Grand Tournament</Typography></Stack>
                <Chip size="small" label={enabled ? 'Tournament enabled' : 'Tournament disabled'} color={enabled ? 'primary' : 'default'} variant={enabled ? 'filled' : 'outlined'} />
            </Stack>
            <LiveTournament status={runtimeStatus} enabled={enabled} catalog={catalog} />
        </Stack></Paper>

        <Paper sx={panelSx}><Stack spacing={2.1}>
            <Box><Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 850 }}>{t('Grand Tournament settings')}</Typography>
                <Typography sx={{ mt: .45, color: '#79a7ef', fontSize: '.8rem' }}>Official AME event ID 129 · only Grand Tournament quests are used here.</Typography></Box>
            <Box sx={{ px: 1.45, py: 1.05, borderRadius: '8px', border: '1px solid rgba(240,195,91,.42)', backgroundColor: 'rgba(146,104,16,.12)' }}>
                <SwitchSetting checked={enabled} onChange={onEnabledChange} label={t('Enable Grand Tournament')}
                    helper="Master switch for tournament quest swaps, quest taking, collection and the optional extra Quest Ticket." />
            </Box>

            <TournamentAccordion title={t('Alliance quests')} subtitle="Swap, take and collect the official 170 AME quest variants." defaultExpanded>
                <Stack spacing={2}>
                    <Box sx={{ px: 1.35, py: 1, borderRadius: '8px', border: '1px solid rgba(129,146,178,.22)' }}>
                        <SwitchSetting checked={settings.swapQuestsEnabled} onChange={value => update('swapQuestsEnabled', value)}
                            label={t('Swap Unwanted Tournament Quests')} helper="Draw a new quest when a free slot contains one of your unwanted Grand Tournament quests." />
                    </Box>
                    <Grid container spacing={1.6}>
                        <Grid item xs={12} md={6}><LabeledField label={t('Rubies When Swapping')} helper="Wait for the free cooldown, or skip it while respecting your reserve.">
                            <TextField select fullWidth value={settings.rubyMode ?? 'watch_limit'} onChange={event => update('rubyMode', event.target.value)} sx={fieldSx}>
                                <MenuItem value="watch_limit">{t('Watch my ruby limit')}</MenuItem><MenuItem value="wait_free">{t('Wait for a free swap')}</MenuItem>
                            </TextField></LabeledField></Grid>
                        <Grid item xs={12} md={6}><LabeledField label={t('Keep At Least This Many Rubies')} helper="No cooldown skip may reduce your balance below this reserve.">
                            <TextField fullWidth type="number" disabled={settings.rubyMode === 'wait_free'} value={settings.keepRubies ?? '100000'} onChange={event => update('keepRubies', event.target.value)} sx={fieldSx} /></LabeledField></Grid>
                    </Grid>
                    <Box sx={{ px: 1.35, py: 1, borderRadius: '8px', border: '1px solid rgba(129,146,178,.22)' }}>
                        <SwitchSetting checked={settings.stopAtEnoughToKeep} onChange={value => update('stopAtEnoughToKeep', value)} label={t('Stop At Enough Quests To Keep')}
                            helper="Stop paid cooldown skips when enough acceptable quests are already visible." />
                    </Box>
                    <LabeledField label={t('Quests To Keep On The Board')} helper="Number of quests not included in the unwanted list.">
                        <TextField fullWidth type="number" value={settings.keepQuestCount ?? '3'} onChange={event => update('keepQuestCount', event.target.value)} inputProps={{ min: 1, max: 24 }} sx={fieldSx} />
                    </LabeledField>
                    <QuestPicker label={t('Quests To Swap')} helper="Pick only Grand Tournament quests you never want the bot to keep." selectedIds={settings.swapQuestIds}
                        onChange={value => update('swapQuestIds', value)} catalog={catalog} loading={loading} loadError={loadError} t={t} />
                    <Box sx={{ px: 1.35, py: 1, borderRadius: '8px', border: '1px solid rgba(129,146,178,.22)' }}>
                        <SwitchSetting checked={settings.takeQuestsEnabled} onChange={value => update('takeQuestsEnabled', value)} label={t('Take On Tournament Quests')}
                            helper="Take a selected quest only when you have no Grand Tournament quest running." />
                    </Box>
                    <QuestPicker label={t('Quests To Take On')} helper="An empty selection means the bot will not take any quest." selectedIds={settings.takeQuestIds}
                        onChange={value => update('takeQuestIds', value)} catalog={catalog} loading={loading} loadError={loadError} t={t} />
                    <Box sx={{ px: 1.35, py: 1, borderRadius: '8px', border: '1px solid rgba(129,146,178,.22)' }}>
                        <SwitchSetting checked={settings.collectFinishedQuests} onChange={value => update('collectFinishedQuests', value)} label={t('Collect Finished Quests')}
                            helper="Collect a completed tournament quest automatically and free its board slot." />
                    </Box>
                </Stack>
            </TournamentAccordion>

            <TournamentAccordion title={t('Quest Ticket')} subtitle="Official package 3902 · one extra ticket per tournament.">
                <Stack spacing={1.8}>
                    <Alert severity="warning" icon={<LocalActivityRoundedIcon />}>The extra ticket costs 7,500 rubies. It is never bought unless this switch is enabled.</Alert>
                    <Box sx={{ px: 1.35, py: 1, borderRadius: '8px', border: '1px solid rgba(129,146,178,.22)' }}>
                        <SwitchSetting checked={settings.buyExtraQuestTicket} onChange={value => update('buyExtraQuestTicket', value)} label={t('Buy The Additional Quest Ticket')}
                            helper="Buy the single event ticket only while event 129 is active and your ruby reserve remains intact." />
                    </Box>
                    <LabeledField label={t('Quest Ticket Ruby Reserve')} helper="The ticket is not bought if 7,500 rubies would bring the balance below this number.">
                        <TextField fullWidth type="number" value={settings.ticketRubyReserve ?? '100000'} onChange={event => update('ticketRubyReserve', event.target.value)} sx={fieldSx} />
                    </LabeledField>
                </Stack>
            </TournamentAccordion>
        </Stack></Paper>
    </Stack>
}
